<script lang="ts">
  import { onMount } from 'svelte';
  import type { AuditEventSummary, TenantSummary } from '@ucc/shared';
  import { ApiError, fetchAudit, fetchTenants } from '$lib/api';
  import { deviceHref, groupHref } from '$lib/nav';
  import { session } from '$lib/session.svelte';
  import Badge from '$lib/components/Badge.svelte';
  import Card from '$lib/components/Card.svelte';

  let events = $state<AuditEventSummary[]>([]);
  let tenants = $state<TenantSummary[]>([]);
  let loading = $state(true);
  let error = $state<string | null>(null);
  let q = $state('');
  let actionFilter = $state('all');
  let tenantFilter = $state('all');

  const isAdmin = $derived(session.user?.role === 'admin');
  const isClient = $derived(session.user?.role === 'client');

  const tenantName = $derived.by(() => {
    const map = new Map(tenants.map((t) => [t.id, t.name]));
    return (id: string | null) => {
      if (!id) return '—';
      return map.get(id) ?? id.slice(0, 8);
    };
  });

  const actionOptions = $derived.by(() => {
    const set = new Set(events.map((e) => e.action).filter(Boolean));
    return Array.from(set).sort();
  });

  const filtered = $derived.by(() => {
    const needle = q.trim().toLowerCase();
    return events.filter((e) => {
      if (actionFilter !== 'all' && e.action !== actionFilter) return false;
      if (tenantFilter !== 'all' && e.tenantId !== tenantFilter) return false;
      if (!needle) return true;
      const hay = [
        e.action,
        e.actorEmail ?? '',
        e.entityType ?? '',
        e.entityId ?? '',
        e.summary ?? '',
        tenantName(e.tenantId),
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(needle);
    });
  });

  function formatWhen(iso: string): string {
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return iso;
      return d.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return iso;
    }
  }

  function actionTone(action: string): 'success' | 'neutral' | 'warning' | 'danger' | 'accent' {
    if (action.includes('clear') || action.includes('delete') || action.includes('revoke')) {
      return 'warning';
    }
    if (action.includes('assign') || action.includes('create') || action.includes('login')) {
      return 'success';
    }
    if (action.includes('fail') || action.includes('deny')) return 'danger';
    return 'accent';
  }

  function entityHref(e: AuditEventSummary): string | null {
    if (!e.entityId) return null;
    if (e.entityType === 'device') return deviceHref(e.entityId);
    if (e.entityType === 'group' || e.entityType === 'device_group') return groupHref(e.entityId);
    return null;
  }

  async function load() {
    loading = true;
    error = null;
    try {
      const [aRes, tRes] = await Promise.all([
        fetchAudit({ limit: 200 }),
        isAdmin
          ? fetchTenants().catch(() => ({ tenants: [] as TenantSummary[] }))
          : Promise.resolve({ tenants: [] as TenantSummary[] }),
      ]);
      events = aRes.events;
      tenants = tRes.tenants;
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        error = 'Unable to load audit log.';
      } else {
        error = err instanceof ApiError ? err.message : 'Failed to load audit events';
      }
      events = [];
    } finally {
      loading = false;
    }
  }

  onMount(() => {
    void load();
  });
</script>

<div class="page">
  <header class="head">
    <div>
      <p class="eyebrow">Security</p>
      <h2>Audit log</h2>
      <p class="sub">
        {#if isClient}
          Your account activity in this tenant (claim, release, and related actions you performed).
        {:else if isAdmin}
          Recent staff actions and system events across tenants.
        {:else}
          Recent staff actions and system events for your tenant.
        {/if}
      </p>
    </div>
    <button type="button" class="refresh" onclick={() => void load()} disabled={loading}>
      {loading ? 'Loading…' : 'Refresh'}
    </button>
  </header>

  <div class="kpis">
    <div class="kpi">
      <span class="kpi-label">Loaded</span>
      <strong>{events.length}</strong>
    </div>
    <div class="kpi">
      <span class="kpi-label">Showing</span>
      <strong>{filtered.length}</strong>
    </div>
  </div>

  <Card title="Events" subtitle="Newest first · max 200" padded={false}>
    <div class="toolbar">
      <label class="field grow">
        <span class="sr-only">Search</span>
        <input type="search" placeholder="Search actor, action, summary…" bind:value={q} />
      </label>
      <label class="field">
        <span class="sr-only">Action</span>
        <select bind:value={actionFilter}>
          <option value="all">All actions</option>
          {#each actionOptions as a (a)}
            <option value={a}>{a}</option>
          {/each}
        </select>
      </label>
      {#if isAdmin && tenants.length > 0}
        <label class="field">
          <span class="sr-only">Tenant</span>
          <select bind:value={tenantFilter}>
            <option value="all">All tenants</option>
            {#each tenants as t (t.id)}
              <option value={t.id}>{t.name}</option>
            {/each}
          </select>
        </label>
      {/if}
    </div>

    {#if error}
      <p class="banner error" role="alert">{error}</p>
    {:else if loading}
      <p class="banner muted">Loading audit events…</p>
    {:else if filtered.length === 0}
      <p class="banner muted">No events match your filters.</p>
    {:else}
      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>When</th>
              <th>Action</th>
              <th>Actor</th>
              {#if isAdmin}<th>Tenant</th>{/if}
              <th>Entity</th>
              <th>Summary</th>
            </tr>
          </thead>
          <tbody>
            {#each filtered as e (e.id)}
              {@const href = entityHref(e)}
              <tr>
                <td class="when" title={e.createdAt}>{formatWhen(e.createdAt)}</td>
                <td>
                  <Badge tone={actionTone(e.action)}>{e.action}</Badge>
                </td>
                <td class="actor">{e.actorEmail ?? '—'}</td>
                {#if isAdmin}
                  <td>{tenantName(e.tenantId)}</td>
                {/if}
                <td class="entity">
                  {#if e.entityType || e.entityId}
                    <span class="etype">{e.entityType ?? 'entity'}</span>
                    {#if href && e.entityId}
                      <a class="eid mono" href={href}>{e.entityId.slice(0, 8)}…</a>
                    {:else if e.entityId}
                      <span class="mono">{e.entityId.slice(0, 8)}…</span>
                    {/if}
                  {:else}
                    —
                  {/if}
                </td>
                <td class="summary">{e.summary ?? '—'}</td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {/if}
  </Card>
</div>

<style>
  .page {
    display: flex;
    flex-direction: column;
    gap: var(--space-5, 1.25rem);
  }

  .head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
  }

  .eyebrow {
    margin: 0 0 0.25rem;
    font-size: var(--text-xs, 0.75rem);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--text-muted);
  }

  h2 {
    margin: 0;
    font-size: var(--text-xl, 1.35rem);
    font-weight: 650;
  }

  .sub {
    margin: 0.35rem 0 0;
    color: var(--text-muted);
    font-size: var(--text-sm, 0.9rem);
  }

  .refresh {
    border: 1px solid var(--border);
    background: var(--surface);
    color: var(--text, #111);
    border-radius: 8px;
    padding: 0.45rem 0.85rem;
    font: inherit;
    cursor: pointer;
  }

  .refresh:disabled {
    opacity: 0.6;
    cursor: default;
  }

  .kpis {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 12rem));
    gap: 0.75rem;
  }

  .kpi {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 0.85rem 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .kpi-label {
    font-size: var(--text-xs, 0.75rem);
    color: var(--text-muted);
  }

  .kpi strong {
    font-size: 1.35rem;
    font-weight: 650;
  }

  .toolbar {
    display: flex;
    flex-wrap: wrap;
    gap: 0.65rem;
    padding: 0.85rem 1rem;
    border-bottom: 1px solid var(--border);
  }

  .field {
    display: block;
    min-width: 9rem;
  }

  .field.grow {
    flex: 1 1 14rem;
    min-width: 12rem;
  }

  input,
  select {
    width: 100%;
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 0.5rem 0.65rem;
    font: inherit;
    background: var(--surface);
    color: inherit;
  }

  .banner {
    margin: 0;
    padding: 1.25rem 1rem;
    font-size: var(--text-sm, 0.9rem);
  }

  .banner.muted {
    color: var(--text-muted);
  }

  .banner.error {
    color: var(--danger, #b91c1c);
  }

  .table-wrap {
    overflow-x: auto;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: var(--text-sm, 0.9rem);
  }

  th,
  td {
    text-align: left;
    padding: 0.7rem 1rem;
    border-bottom: 1px solid var(--border);
    vertical-align: top;
  }

  th {
    font-size: var(--text-xs, 0.75rem);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--text-muted);
    font-weight: 600;
    background: var(--surface-2, #f9fafb);
    white-space: nowrap;
  }

  .when {
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
    color: var(--text-muted);
    font-size: 0.85em;
  }

  .actor {
    word-break: break-all;
  }

  .entity {
    white-space: nowrap;
  }

  .etype {
    display: inline-block;
    margin-right: 0.35rem;
    font-size: var(--text-xs, 0.75rem);
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  .eid {
    color: inherit;
    text-decoration: none;
  }

  .eid:hover {
    color: var(--accent, #2563eb);
    text-decoration: underline;
  }

  .mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 0.85em;
  }

  .summary {
    max-width: 28rem;
    color: var(--text, #111);
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    border: 0;
  }
</style>
